import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import "../assets/css/AddLead.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/Issues.css";

function safeString(v) {
  return v == null ? "" : String(v);
}

function unwrapAndClean(v) {
  if (v == null) return "";
  const s = safeString(v).trim();
  // Remove outer backend quotes if present
  let out = s;
  if (out.length >= 2 && out[0] === '"' && out[out.length - 1] === '"') {
    out = out.slice(1, -1).replace(/\\"/g, '"');
  }
  // Some backend strings are malformed like "\"4545436'"
  // Strip leading/trailing quotes/backslashes/apostrophes.
  return out.replace(/^[\\'"`]+|[\\'"`]+$/g, "");
}

function getCreatedBy() {
  try {
    const userStr = localStorage.getItem("user");
    if (!userStr) return 1;
    const user = JSON.parse(userStr);
    return user.user_id || user.id || 1;
  } catch {
    return 1;
  }
}

function getId(v) {
  if (v == null || v === "") return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

function parseDecimal(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim().replace(/,/g, ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

function toISODateOnly(v) {
  // Backend gives yyyy-mm-dd; datetime-local in many forms gives yyyy-mm-ddTHH:mm
  if (!v) return "";
  const s = String(v).trim();
  if (s.includes("T")) return s.split("T")[0];
  return s;
}

function EditInternshipLead({ leadId, onBack, onUpdateLead }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const createdBy = getCreatedBy();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    altPhone: "",
    qualificationId: "",
    availableFrom: "",
    collegeName: "",
    designationId: "",
    yearOfPassing: "",
    internshipDuration: "",
    skills: "",
    preferredLocation: "",
    sourceId: "",
    statusId: "",
    addressLine1: "",
    addressLine2: "",
    cityId: "",
    stateId: "",
    countryId: "",
    zipCode: "",
  });

  const [phoneCountryCode, setPhoneCountryCode] = useState("+91");
  const [resumeFile, setResumeFile] = useState(null);

  const [qualifications, setQualifications] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const phoneCountryOptions = useMemo(() => {
    return (countries || []).map((c) => ({
      id: c.country_id ?? c.Country_Id ?? c.id,
      code: (c.country_code || c.country_short_code || "").trim(),
      name: c.country_name || c.Country_Name || c.name || "",
    }));
  }, [countries]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  useEffect(() => {
    const fetchLookups = async () => {
      setLoadingCountries(true);
      try {
        const [qualRes, desRes, sourceRes, statusRes, countriesRes] =
          await Promise.all([
            axiosInstance.get("/qualifications"),
            axiosInstance.get("/designations"),
            axiosInstance.get("/lead-sources"),
            axiosInstance.get("/lead-statuses"),
            axiosInstance.get("/countries"),
          ]);

        const rawQuals = Array.isArray(qualRes.data)
          ? qualRes.data
          : Array.isArray(qualRes.data?.data)
            ? qualRes.data.data
            : [];
        setQualifications(
          rawQuals
            .map((q) => ({
              id: q.Qualification_Id ?? q.qualification_id ?? q.id,
              name: q.Qualification_Name ?? q.qualification_name ?? q.name ?? "",
            }))
            .filter((q) => q.id != null && String(q.name).trim() !== "")
        );

        const rawDesignations = Array.isArray(desRes.data)
          ? desRes.data
          : Array.isArray(desRes.data?.data)
            ? desRes.data.data
            : [];
        setDesignations(
          rawDesignations
            .map((d) => ({
              id: d.Designation_Id ?? d.designation_id ?? d.id,
              name: d.Designation_Name ?? d.designation_name ?? d.name ?? "",
            }))
            .filter((d) => d.id != null && String(d.name).trim() !== "")
        );

        const rawSources = Array.isArray(sourceRes.data)
          ? sourceRes.data
          : Array.isArray(sourceRes.data?.data)
            ? sourceRes.data.data
            : [];
        setSources(
          rawSources
            .map((s) => ({
              id: s.Source_Id ?? s.source_id ?? s.id,
              name: s.Source_Name ?? s.source_name ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "")
        );

        const rawStatuses = Array.isArray(statusRes.data)
          ? statusRes.data
          : Array.isArray(statusRes.data?.data)
            ? statusRes.data.data
            : [];
        setStatuses(
          rawStatuses
            .map((s) => ({
              id: s.Status_Id ?? s.status_id ?? s.id,
              name: s.Status ?? s.status ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "")
        );

        const rawCountries = Array.isArray(countriesRes.data)
          ? countriesRes.data
          : Array.isArray(countriesRes.data?.data)
            ? countriesRes.data.data
            : [];
        setCountries(rawCountries);
      } catch (err) {
        console.error("Error fetching internship lead lookups:", err);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchLookups();
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/internship-lead/${leadId}`);
        const il =
          response.data?.internship_lead ||
          response.data?.internshipLead ||
          response.data?.internship_lead_data ||
          response.data;

        const address = il.address || {};

        const nextCountryId = il.Country_Id ?? il.country_id ?? address.Country_Id ?? address.country_id ?? "";
        const nextStateId = il.State_Id ?? il.state_id ?? address.State_Id ?? address.state_id ?? "";
        const nextCityId = il.City_Id ?? il.city_id ?? address.City_Id ?? address.city_id ?? "";

        setPhoneCountryCode(unwrapAndClean(il.Country_Code) || "+91");
        setFormData({
          firstName: unwrapAndClean(il.First_Name) || il.First_Name || "",
          lastName: unwrapAndClean(il.Last_Name) || il.Last_Name || "",
          email: unwrapAndClean(il.Email) || il.Email || "",
          phone: unwrapAndClean(il.Phone) || il.Phone || "",
          altPhone: unwrapAndClean(il.Alt_Phone) || il.Alt_Phone || "",
          qualificationId: String(il.Qualification_Id ?? il.qualification_id ?? il.QualificationId ?? "") || "",
          availableFrom: toISODateOnly(il.Available_From) || "",
          collegeName: unwrapAndClean(il.College_Name) || il.College_Name || "",
          designationId: String(il.Designation_Id ?? il.designation_id ?? il.DesignationId ?? "") || "",
          yearOfPassing: unwrapAndClean(il.Year_Of_Passing) || il.Year_Of_Passing || "",
          internshipDuration: unwrapAndClean(il.Internship_Duration) || il.Internship_Duration || "",
          skills: unwrapAndClean(il.Skills) || il.Skills || "",
          preferredLocation: unwrapAndClean(il.Preferred_Location) || il.Preferred_Location || "",
          sourceId: String(il.Source_Id ?? il.source_id ?? "") || "",
          statusId: String(il.Status_Id ?? il.status_id ?? "") || "",
          addressLine1: unwrapAndClean(address.Address_Line_1) || address.Address_Line_1 || "",
          addressLine2: unwrapAndClean(address.Address_Line_2) || address.Address_Line_2 || "",
          cityId: String(nextCityId || "") || "",
          stateId: String(nextStateId || "") || "",
          countryId: String(nextCountryId || "") || "",
          zipCode: unwrapAndClean(address.Zip_Code) || address.Zip_Code || "",
        });
      } catch (err) {
        console.error("Error fetching internship lead for edit:", err);
        alert("Failed to load internship lead for editing.");
      } finally {
        setLoading(false);
      }
    };

    if (leadId != null) load();
  }, [leadId]);

  useEffect(() => {
    const countryId = getId(formData.countryId);
    if (!countryId) {
      setStates([]);
      setCities([]);
      return;
    }

    const fetchStates = async () => {
      setLoadingStates(true);
      try {
        const response = await axiosInstance.get(`/states/${countryId}`);
        const raw = response.data;
        const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setStates(arr);
      } catch (err) {
        console.error("Error fetching states:", err);
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, [formData.countryId]);

  useEffect(() => {
    const stateId = getId(formData.stateId);
    if (!stateId) {
      setCities([]);
      return;
    }

    const fetchCities = async () => {
      setLoadingCities(true);
      try {
        const response = await axiosInstance.get(`/cities/${stateId}`);
        const raw = response.data;
        const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setCities(arr);
      } catch (err) {
        console.error("Error fetching cities:", err);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCities();
  }, [formData.stateId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredText = [
      ["firstName", formData.firstName],
      ["phone", formData.phone],
      ["sourceId", formData.sourceId],
    ];

    const missing = requiredText
      .filter(([, v]) => !safeString(v).trim())
      .map(([k]) => k);

    if (missing.length) {
      alert("Please fill all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData();

      payload.append("First_Name", formData.firstName);
      payload.append("Last_Name", formData.lastName || "");
      payload.append("Email", formData.email);
      payload.append("Country_Code", safeString(phoneCountryCode));
      payload.append("Phone", safeString(formData.phone));
      payload.append("Alt_Phone", formData.altPhone || "");

      payload.append("Skills", formData.skills || "");
      payload.append("Preferred_Location", formData.preferredLocation || "");

      payload.append(
        "Qualification_Id",
        getId(formData.qualificationId) != null
          ? String(getId(formData.qualificationId))
          : ""
      );
      payload.append("Available_From", formData.availableFrom);
      payload.append("College_Name", formData.collegeName || "");

      payload.append(
        "Designation_Id",
        getId(formData.designationId) != null
          ? String(getId(formData.designationId))
          : ""
      );
      payload.append("Year_Of_Passing", formData.yearOfPassing || "");
      payload.append("Internship_Duration", formData.internshipDuration || "");

      payload.append(
        "Source_Id",
        getId(formData.sourceId) != null ? String(getId(formData.sourceId)) : ""
      );
      payload.append(
        "Status_Id",
        getId(formData.statusId) != null ? String(getId(formData.statusId)) : ""
      );

      payload.append("Address_Line_1", formData.addressLine1 || "");
      payload.append("Address_Line_2", formData.addressLine2 || "");
      payload.append(
        "City_Id",
        getId(formData.cityId) != null ? String(getId(formData.cityId)) : ""
      );
      payload.append(
        "State_Id",
        getId(formData.stateId) != null ? String(getId(formData.stateId)) : ""
      );
      payload.append(
        "Country_Id",
        getId(formData.countryId) != null ? String(getId(formData.countryId)) : ""
      );
      payload.append("Zip_Code", formData.zipCode || "");

      // Backend may require Resume on update; we only send it if user picked a file.
      if (resumeFile) {
        payload.append("Resume", resumeFile);
      }

      payload.append("Updated_By", String(createdBy));

      // Other lead updates use a dedicated update endpoint (e.g. /update-job-lead/:id).
      // Use /update-internship-lead/:id as the likely correct route.
      let response;
      try {
        response = await axiosInstance.put(`/update-internship-lead/${leadId}`, payload);
      } catch (err) {
        // Fallback to the legacy endpoint if your backend uses it for updates
        // (in case the update-internship-lead route is not enabled).
        const status = err?.response?.status;
        if (status === 405) {
          response = await axiosInstance.put(`/internship-lead/${leadId}`, payload);
        } else {
          throw err;
        }
      }
      const msg =
        response.data?.message ||
        response.data?.error ||
        "Internship lead updated successfully.";
      alert(msg);

      if (onUpdateLead) onUpdateLead();
      if (onBack) onBack();
    } catch (err) {
      console.error("Error updating internship lead:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to update internship lead.";
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="editlead-container internship-lead-page">
        <div className="editlead-loading">Loading internship lead details...</div>
      </div>
    );
  }

  return (
    <div className="editlead-container internship-lead-page">
      <button type="button" className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back
      </button>

      <div className="issues-main-card">
      <form className="editlead-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-grid internship-lead-main-grid">
            <div className="form-group">
              <label>
                First Name <span className="required">*</span>
              </label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>
                Email
              </label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>
                Phone <span className="required">*</span>
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                  style={{ maxWidth: 120 }}
                  required
                >
                  <option value="+91">+91</option>
                  {phoneCountryOptions
                    .filter((x) => x.code)
                    .map((c) => (
                      <option key={c.id ?? c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                </select>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-group">
              <label>Alt Phone</label>
              <input type="tel" name="altPhone" value={formData.altPhone} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>
                Qualification
              </label>
              <select name="qualificationId" value={formData.qualificationId} onChange={handleChange}>
                <option value="">Select</option>
                {qualifications.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Available From
              </label>
              <input type="date" name="availableFrom" value={formData.availableFrom} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>
                College Name
              </label>
              <input type="text" name="collegeName" value={formData.collegeName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>
                Designation
              </label>
              <select name="designationId" value={formData.designationId} onChange={handleChange}>
                <option value="">Select</option>
                {designations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Year Of Passing
              </label>
              <input type="text" name="yearOfPassing" value={formData.yearOfPassing} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>
                Internship Duration
              </label>
              <input type="text" name="internshipDuration" value={formData.internshipDuration} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>
                Skills
              </label>
              <input type="text" name="skills" value={formData.skills} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>
                Preferred Location
              </label>
              <input type="text" name="preferredLocation" value={formData.preferredLocation} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>
                Source <span className="required">*</span>
              </label>
              <select name="sourceId" value={formData.sourceId} onChange={handleChange} required>
                <option value="">Select</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Status
              </label>
              <select name="statusId" value={formData.statusId} onChange={handleChange}>
                <option value="">Select</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Address Line 1
              </label>
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>
                Country
              </label>
              <select
                name="countryId"
                value={formData.countryId}
                onChange={(e) => setFormData((p) => ({ ...p, countryId: e.target.value, stateId: "", cityId: "" }))}
              >
                <option value="">Select</option>
                {countries.map((c) => (
                  <option key={c.country_id ?? c.Country_Id ?? c.id} value={c.country_id ?? c.Country_Id ?? c.id}>
                    {c.country_name || c.Country_Name || c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                State
              </label>
              <select
                name="stateId"
                value={formData.stateId}
                onChange={(e) => setFormData((p) => ({ ...p, stateId: e.target.value, cityId: "" }))}
              >
                <option value="">{loadingStates ? "Loading..." : "Select"}</option>
                {states.map((s) => (
                  <option key={s.state_id ?? s.State_Id ?? s.id} value={s.state_id ?? s.State_Id ?? s.id}>
                    {s.state_name || s.State_Name || s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                City
              </label>
              <select name="cityId" value={formData.cityId} onChange={(e) => setFormData((p) => ({ ...p, cityId: e.target.value }))}>
                <option value="">{loadingCities ? "Loading..." : "Select"}</option>
                {cities.map((c) => (
                  <option key={c.city_id ?? c.City_Id ?? c.id} value={c.city_id ?? c.City_Id ?? c.id}>
                    {c.city_name || c.City_Name || c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Zip Code
              </label>
              <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Resume (optional)</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt,.csv"
                onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-add-lead" disabled={submitting}>
            {submitting ? "Updating..." : "Update Internship Lead"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default EditInternshipLead;

